import React from 'react';
import { Link } from 'react-router-dom';
import styles from './Pages.module.css';

const CookiePolicy = () => {
  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.container}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1>Cookie Policy</h1>
        </div>
      </div>
      
      <div className={styles.pageContent}>
        <div className={styles.container}>
          <section className={styles.section}>
            <h2>1. Introduction</h2>
            <p>
              This Cookie Policy explains how NexusStake ("we", "us", or "our") uses cookies and similar technologies 
              to recognize you when you visit our cryptocurrency staking platform ("Platform"). It explains what these 
              technologies are and why we use them, as well as your rights to control our use of them.
            </p>
            <p>
              By using our Platform, you agree to our use of cookies as described in this policy. If you do not agree with 
              our use of cookies, you should set your browser settings accordingly or refrain from using our Platform.
            </p>
          </section>
          
          <section className={styles.section}>
            <h2>2. What Are Cookies</h2>
            <p>
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a 
              website. They are widely used to make websites work efficiently and provide information to the website owners.
            </p>
            <p>
              Cookies set by the website you are visiting are called "first-party cookies." Cookies set by parties other than the 
              website you are visiting are called "third-party cookies." Third-party cookies enable certain features or functionality 
              to be provided on or through the website (e.g., analytics, advertising, and social media features). Third-party cookies 
              can recognize your computer both when it visits the respective website and when it visits certain other websites.
            </p>
          </section>
          
          <section className={styles.section}>
            <h2>3. Why We Use Cookies</h2>
            <p>
              We use first-party and third-party cookies for several reasons. Some cookies are required for technical reasons for 
              our Platform to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies allow us 
              to track and target the interests of our users to enhance the experience on our Platform. Third parties use cookies 
              through our Platform for advertising, analytics, and other purposes.
            </p>
          </section>
          
          <section className={styles.section}>
            <h2>4. Types of Cookies We Use</h2>
            <p>The cookies we use can be categorized as follows:</p>
            
            <h3>4.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the Platform to function and cannot be switched off in our systems. They are usually 
              only set in response to actions made by you which amount to a request for services, such as setting your privacy 
              preferences, logging in, or filling in forms. You can set your browser to block or alert you about these cookies, 
              but some parts of the Platform may not work then.
            </p>
            
            <h3>4.2 Performance Cookies</h3>
            <p>
              These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our 
              Platform. They help us to know which pages are the most and least popular and see how visitors move around the Platform. 
              All information these cookies collect is aggregated and therefore anonymous.
            </p>
            
            <h3>4.3 Functional Cookies</h3>
            <p>
              These cookies enable the Platform to provide enhanced functionality and personalization. They may be set by us or by 
              third-party providers whose services we have added to our pages. If you do not allow these cookies, some or all of 
              these services may not function properly.
            </p>
            
            <h3>4.4 Targeting Cookies</h3>
            <p>
              These cookies may be set through our Platform by our advertising partners. They may be used by those companies to build 
              a profile of your interests and show you relevant advertisements on other sites. They do not directly store personal 
              information but are based on uniquely identifying your browser and internet device.
            </p>
          </section>
          
          <section className={styles.section}>
            <h2>5. Specific Cookies We Use</h2>
            <div className={styles.tableContainer}>
              <table className={styles.cookieTable}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>_ga</td>
                    <td>Google Analytics</td>
                    <td>Used to distinguish users</td>
                    <td>2 years</td>
                    <td>Performance</td>
                  </tr>
                  <tr>
                    <td>_gid</td>
                    <td>Google Analytics</td>
                    <td>Used to distinguish users</td>
                    <td>24 hours</td>
                    <td>Performance</td>
                  </tr>
                  <tr>
                    <td>_gat</td>
                    <td>Google Analytics</td>
                    <td>Used to throttle request rate</td>
                    <td>1 minute</td>
                    <td>Performance</td>
                  </tr>
                  <tr>
                    <td>connect.sid</td>
                    <td>NexusStake</td>
                    <td>Session identifier for authenticated users</td>
                    <td>Session</td>
                    <td>Essential</td>
                  </tr>
                  <tr>
                    <td>darkMode</td>
                    <td>NexusStake</td>
                    <td>Saves user preferences for dark mode</td>
                    <td>1 year</td>
                    <td>Functional</td>
                  </tr>
                  <tr>
                    <td>language</td>
                    <td>NexusStake</td>
                    <td>Saves user language preferences</td>
                    <td>1 year</td>
                    <td>Functional</td>
                  </tr>
                  <tr>
                    <td>walletConnected</td>
                    <td>NexusStake</td>
                    <td>Tracks wallet connection status</td>
                    <td>Session</td>
                    <td>Essential</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
          
          <section className={styles.section}>
            <h2>6. Other Tracking Technologies</h2>
            <p>In addition to cookies, we may also use other similar technologies to track user activity on our Platform:</p>
            
            <h3>6.1 Web Beacons (Tracking Pixels)</h3>
            <p>
              Web beacons are small graphic images (also known as "tracking pixels" or "clear GIFs") that may be embedded in our 
              Platform and email messages. They can be used for things such as recording the pages and advertisements that users click on, 
              or tracking the performance of email marketing campaigns.
            </p>
            
            <h3>6.2 Local Storage</h3>
            <p>
              We may use local storage technologies (such as localStorage and sessionStorage) to store certain information about your 
              interaction with our Platform. Unlike cookies, local storage data is not automatically transferred to the server with 
              each page request.
            </p>
            
            <h3>6.3 Scripts</h3>
            <p>
              We may use JavaScript and other scripting technologies to improve the functionality of our Platform and collect 
              information about your usage of our Platform.
            </p>
          </section>
          
          <section className={styles.section}>
            <h2>7. How to Control Cookies</h2>
            <p>
              You have the right to decide whether to accept or reject cookies. Below is an explanation of how you can exercise this right:
            </p>
            
            <h3>7.1 Browser Settings</h3>
            <p>
              Most web browsers allow control of most cookies through browser settings. You can set your browser to reject all or some 
              cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts 
              of our Platform may become inaccessible or not function properly.
            </p>
            <p>
              For more information about how to change your browser settings to block or filter cookies, visit 
              <a href="http://www.aboutcookies.org" target="_blank" rel="noopener noreferrer"> AboutCookies.org</a> or 
              <a href="http://www.cookiecentral.com" target="_blank" rel="noopener noreferrer"> CookieCentral.com</a>.
            </p>
            
            <h3>7.2 Third-Party Tools</h3>
            <p>
              For some cookies placed by third parties, you may have opt-out options directly with the provider.
            </p>
            <p>
              For example, you can opt out of Google Analytics without affecting how you visit our Platform by using the 
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer"> Google Analytics Opt-out Browser Add-on</a>.
            </p>
          </section>
          
          <section className={styles.section}>
            <h2>8. Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect, for example, changes to the cookies we use or for other 
              operational, legal, or regulatory reasons. Please regularly revisit this page to stay informed about our use of cookies 
              and related technologies.
            </p>
            <p>
              The date at the top of this Policy indicates when it was last updated.
            </p>
          </section>
          
          <section className={styles.section}>
            <h2>9. Contact Information</h2>
            <p>
              If you have any questions about our use of cookies or other technologies, please contact us:
            </p>
            <ul className={styles.contactInfo}>
              <li>By email: privacy@nexusstake.com</li>
              <li>By mail: NexusStake, 123 Blockchain Street, Crypto City, 12345, European Union</li>
            </ul>
            <p>
              For more information about how we use and protect your information, please refer to our 
              <Link to="/privacy"> Privacy Policy</Link>.
            </p>
          </section>
          
          <div className={styles.lastUpdated}>
            Last updated: April 30, 2023
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy; 